/**
 * DocumentUploadInput - source picker for a warranty template document.
 *
 * Exposes a two-tab control that lets the user either upload a file
 * (via the /files/upload/ endpoint, use_case=WARRANTY_DOCUMENT) or
 * paste an external URL. The parent component controls the value and
 * receives updates via onChange.
 */

import { useState } from 'react'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { useUploadFile } from '@/admin/hooks/use-products'
import { FileUploader } from '@/shared/components/ui/file-uploader'
import { humanizeAccept, type UploadedFileItem } from '@/shared/lib/file-types'
import { toastApiError } from '@/shared/lib/api-error'

export interface DocumentSource {
  uploaded_file_uuid: string | null
  uploaded_file_name: string | null
  url: string | null
}

const DOCUMENT_ACCEPT =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,text/plain'

interface DocumentUploadInputProps {
  value: DocumentSource
  onChange: (value: DocumentSource) => void
  label?: string
  /** File use case for the upload. */
  useCase?: string
  /** Offer a link instead of a file; off where a real file must be attached. */
  allowUrl?: boolean
}

export function DocumentUploadInput({
  value,
  onChange,
  label = 'Source',
  useCase = 'WARRANTY_DOCUMENT',
  allowUrl = true,
}: DocumentUploadInputProps) {
  const upload = useUploadFile()
  const [activeTab, setActiveTab] = useState<'file' | 'url'>(() =>
    value.uploaded_file_uuid ? 'file' : allowUrl && value.url ? 'url' : 'file',
  )

  const committed: UploadedFileItem[] = value.uploaded_file_uuid
    ? [
        {
          uuid: value.uploaded_file_uuid,
          file_name: value.uploaded_file_name ?? 'Document',
        },
      ]
    : []

  const uploadFn = async (file: File): Promise<UploadedFileItem> => {
    try {
      const result = await upload.mutateAsync({ file, use_case: useCase })
      return {
        uuid: result.uuid,
        file_name: result.file_name,
        file_url: result.file_url,
        mime_type: (result as { mime_type?: string }).mime_type,
      }
    } catch (err) {
      toastApiError(err, 'File upload failed. Please try again.')
      throw err
    }
  }

  const handleFilesChange = (files: UploadedFileItem[]) => {
    const f = files[0]
    onChange(
      f
        ? { uploaded_file_uuid: f.uuid, uploaded_file_name: f.file_name, url: null }
        : { uploaded_file_uuid: null, uploaded_file_name: null, url: null },
    )
  }

  function handleUrlChange(url: string) {
    onChange({ uploaded_file_uuid: null, uploaded_file_name: null, url })
  }

  function handleTabChange(tab: string | null) {
    if (tab !== 'file' && tab !== 'url') return
    setActiveTab(tab)
    if (tab === 'file' && value.url) {
      onChange({ uploaded_file_uuid: null, uploaded_file_name: null, url: null })
    }
    if (tab === 'url' && value.uploaded_file_uuid) {
      onChange({ uploaded_file_uuid: null, uploaded_file_name: null, url: '' })
    }
  }

  return (
    <div>
      {label && <Label className="clara-section-label mb-1.5 block">{label}</Label>}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        {allowUrl && (
          <TabsList className="mb-2">
            <TabsTrigger value="file">File</TabsTrigger>
            <TabsTrigger value="url">URL</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="file">
          <FileUploader
            value={committed}
            onChange={handleFilesChange}
            uploadFn={uploadFn}
            accept={DOCUMENT_ACCEPT}
            acceptLabel={humanizeAccept(DOCUMENT_ACCEPT)}
          />
        </TabsContent>

        <TabsContent value="url">
          <Input
            type="url"
            placeholder="https://example.com/booklet.pdf"
            value={value.url ?? ''}
            onChange={(e) => handleUrlChange(e.target.value)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
